import {Finding, mapFindingToGeoJSONProperties} from "../../model/Finding";
import * as rt from "@tanstack/react-table";
import React, {useCallback, useMemo, useState} from "react";
import {AppTable, DateRangeFilter, EqualsStringOrInArray, RowActions} from "../../components/AppTable";
import * as bp from "@blueprintjs/core";
import {openAllGeoJson, openGeoJson} from "../../utils/geojson";
import {RenderTaleProps} from "../main/MainView";
import {DateTime} from "luxon";
import {ExportFn} from "../../components/ActionsBar";
import FindingsService from "../../service/FindingsService";
import {fetchWithToken} from "../../utils/fetchWithToken";
import {FindingsDialog, FindingsDialogProps} from "./FindingsDialog";

export default function FindingsTable({data, refreshData: refreshFindings}: RenderTaleProps<Finding>) {
    const [dialogProps, setDialogProps] = useState<FindingsDialogProps>({
        isOpen: false,
        // keep this callback stable and avoid capturing stale dialogProps
        close: async () => {
            setDialogProps(prev => ({...prev, isOpen: false}));
            await refreshFindings();
        },
        mode: 'new'
    });

    const openCreateDialog = useCallback(() => setDialogProps(prev => ({
        ...prev,
        isOpen: true,
        mode: 'new'
    })), []);
    const columnHelper = rt.createColumnHelper<Finding>();
    const handleDelete = useCallback(async (finding: Finding) => {
        await FindingsService.deleteFinding(finding);
        await refreshFindings();
    }, [refreshFindings]);

    const columns = useMemo(() => [
        columnHelper.accessor('area.name', {
            cell: info => info.getValue(),
            header: () => <span>Schutzgebiet</span>,
            filterFn: EqualsStringOrInArray
        }),
        columnHelper.group({
            columns: [
                columnHelper.accessor('plantType.name', {
                    cell: info => info.getValue(),
                    header: 'Name',
                    filterFn: EqualsStringOrInArray
                }),
                columnHelper.accessor('plantType.scientificName', {
                    cell: info => info.getValue(),
                    header: 'Wissenschaftlicher Name',
                    filterFn: EqualsStringOrInArray
                })
            ],
            header: 'Pflanzenart'
        }),
        columnHelper.accessor('amount', {
            cell: info => info.getValue(),
            header: () => <span>Anzahl</span>
        }),
        columnHelper.accessor('polygon', {
            cell: info =>
                <bp.Button icon="map"
                           text="Öffnen"
                           onClick={() => openGeoJson(info.getValue(), mapFindingToGeoJSONProperties(info.row.original))}/>,
            header: () => <span>Karte</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('created', {
            cell: info => DateTime.fromISO(info.getValue()).toLocaleString({}),
            header: () => <span>Erstellt am</span>,
            filterFn: DateRangeFilter
        }),
        columnHelper.accessor('user.email', {
            cell: info => info.getValue(),
            header: () => <span>Erstellt von</span>,
            filterFn: EqualsStringOrInArray
        }),
        columnHelper.accessor('creationMode', {
            cell: info => {
                const creationMode = info.getValue();
                if(creationMode === 'WEB') {
                    return <bp.Tag>web</bp.Tag>;
                } else if(creationMode === 'MOBILE') {
                    return <bp.Tag>mobile</bp.Tag>;
                }
                return '';
            },
            header: () => <span>Erstellt auf</span>,
            filterFn: EqualsStringOrInArray
        }),
        columnHelper.display({
            id: 'actions',
            cell: info =>
                <RowActions delete={async () => handleDelete(info.row.original)}/>,
            header: () => <span>Aktionen</span>
        })
    ], [handleDelete]);
    const [sorting, setSorting] = useState<rt.SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<rt.ColumnFiltersState>([]);
    const [pagination, setPagination] = useState<rt.PaginationState>({ pageIndex: 0, pageSize: 50 });
    const table = rt.useReactTable({
        data: data,
        columns,
        state: {
            sorting,
            columnFilters,
            pagination
        },
        getCoreRowModel: rt.getCoreRowModel(),
        getSortedRowModel: rt.getSortedRowModel(),
        getFilteredRowModel: rt.getFilteredRowModel(),
        getPaginationRowModel: rt.getPaginationRowModel(),
        // Prevent automatic page reset when data changes (e.g., due to live refresh)
        autoResetPageIndex: false,
        onSortingChange: (updater) => {
            setSorting(updater);
            setPagination(prev => ({ ...prev, pageIndex: 0 }));
        },
        onColumnFiltersChange: (updater) => {
            setColumnFilters(updater);
            setPagination(prev => ({ ...prev, pageIndex: 0 }));
        },
        onPaginationChange: setPagination
    });

    const exportCsv = useCallback(async () => {
        const response = await fetchWithToken(process.env.REACT_APP_API + '/api/v2/findings/export/csv', {
            method: 'POST',
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(table.getFilteredRowModel().flatRows.map(row => row.original.id))
        });

        if (response.status !== 200) {
            throw new Error('Oh no!'); // FIXME - More specific error handling
        }

        const blob = await response.blob();
        const fileUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');
        downloadLink.href = fileUrl;
        downloadLink.download = "export.csv";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(fileUrl);
    }, [table]);

    const exportKml = useCallback(async () => {
        const response = await fetchWithToken(process.env.REACT_APP_API + '/api/v2/findings/export/kml', {
            method: 'POST',
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(table.getFilteredRowModel().flatRows.map(row => row.original.id))
        });

        if (response.status !== 200) {
            throw new Error('Oh no!'); // FIXME - More specific error handling
        }

        const blob = await response.blob();
        const fileUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');
        downloadLink.href = fileUrl;
        downloadLink.download = "export.kml";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(fileUrl);
    }, [table]);

    const exportGeoJson = useCallback(async () => {
        const response = await fetchWithToken(process.env.REACT_APP_API + '/api/v2/findings/export/geojson', {
            method: 'POST',
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(table.getFilteredRowModel().flatRows.map(row => row.original.id))
        });

        if (response.status !== 200) {
            throw new Error('Oh no!'); // FIXME - More specific error handling
        }

        const blob = await response.blob();
        const fileUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');
        downloadLink.href = fileUrl;
        downloadLink.download = "export.json";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(fileUrl);
    }, [table]);

    const exportFns: ExportFn[] = useMemo(() => ([
        { label: '.csv', fn: exportCsv },
        { label: '.kml', fn: exportKml },
        { label: 'GeoJSON', fn: exportGeoJson },
    ]), [exportCsv, exportKml, exportGeoJson]);
    const openAll = useCallback(async () => {
        openAllGeoJson(table.getFilteredRowModel()
            .flatRows.map(row => row.original)
            .map(f => {return {geojson: f.polygon, properties: mapFindingToGeoJSONProperties(f)};})
        );
    }, [table]);
    return <>
        <AppTable table={table}
                  create={openCreateDialog}
                  exportFns={exportFns}
                  openAll={openAll}
                  showPagination
                  pageSizeOptions={[10, 25, 50, 100]}/>
        <FindingsDialog {...dialogProps} />
    </>;
}
